import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Autocomplete, TextField } from '@mui/material';

import DraftBoard from '../components/DraftBoard';
import ScoutSuggestionPanel from '../games/dota/components/ScoutSuggestionPanel';
import supabaseService from '../games/dota/services/SupabaseService';
import { teamSlots, predictNextMove } from '../games/dota/utils/draftPredictor';
import { useDraft } from '../contexts/DraftContext';
import HeroDataModule from '../games/dota/models/HeroData';

const { getHeroByName } = HeroDataModule;

// First-pick-side slots (left column on the board). Team owns these when it
// drafts 1st pick; otherwise it owns the rest (2nd-pick side).
const FP_SLOTS = new Set(teamSlots('fp', 'NEW').map((s) => s.slot));

const norm = (s) => (s || '').trim().toLowerCase();
// The opponent's name in a pro_matches row, relative to the scouted team.
const opponentOf = (row, team) => (norm(row.radiant_name) === norm(team) ? row.dire_name : row.radiant_name);

// ── styles ──────────────────────────────────────────────────────────────────
const Page = styled.div`
  min-height: 100vh;
  background: #0a0c12;
  color: #e2e8f0;
  padding: 16px;
  box-sizing: border-box;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
  flex-wrap: wrap;
`;

const TitleWrap = styled.div``;
const H1 = styled.h1`
  font-size: 20px;
  margin: 0;
  color: #1de9b6;
`;
const Tagline = styled.div`
  font-size: 12px;
  color: #94a3b8;
`;

const BackLink = styled.button`
  background: none; border: 1px solid #2a3346; color: #cbd5e1;
  border-radius: 8px; padding: 8px 12px; cursor: pointer; font-size: 13px;
  &:hover { border-color: #1de9b6; color: #1de9b6; }
`;

const Setup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  background: #0e1118;
  border: 1px solid #2a3346;
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 16px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;
const FieldLabel = styled.label`
  font-size: 11px;
  color: #64748b;
  letter-spacing: 0.5px;
`;

const Toggle = styled.div`
  display: inline-flex;
  border: 1px solid #2a3346;
  border-radius: 8px;
  overflow: hidden;
`;
const ToggleBtn = styled.button`
  background: ${(p) => (p.$on ? '#1de9b6' : 'transparent')};
  color: ${(p) => (p.$on ? '#08221c' : '#cbd5e1')};
  border: none; padding: 9px 14px; cursor: pointer; font-size: 13px; font-weight: 600;
`;

const Select = styled.select`
  background: #11151f; color: #fff; border: 1px solid #2a3346;
  border-radius: 8px; padding: 9px 10px; font-size: 13px; min-height: 40px;
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  gap: 16px;
  align-items: start;
  @media (max-width: 1000px) { grid-template-columns: 1fr; }
`;

const BoardHost = styled.div`
  min-width: 0;
  overflow-x: auto;
`;

const PanelCol = styled.div`
  position: sticky;
  top: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  @media (max-width: 1000px) { position: static; }
`;

const PoolNote = styled.div`
  font-size: 11px; color: #64748b;
`;

const Placeholder = styled.div`
  background: #0e1118; border: 1px dashed #2a3346; border-radius: 10px;
  padding: 28px 16px; text-align: center; color: #64748b;
`;

const PATCH_OPTIONS = {
  current: 'Current patch (since Dec 2025)',
  '6mo': 'Last 6 months',
  '12mo': 'Last 12 months',
  all: 'All history',
};
function windowFromDate(key) {
  if (key === 'current') return '2025-12-16';
  if (key === 'all') return null;
  const d = new Date();
  d.setMonth(d.getMonth() - (key === '6mo' ? 6 : 12));
  return d.toISOString().slice(0, 10);
}

export default function ScoutSimulator() {
  const navigate = useNavigate();
  const { pickMode, scenariosFirstPick, scenariosSecondPick, updateScenario } = useDraft();

  const [teams, setTeams] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState(null); // {team, matches}
  const [opponentTeam, setOpponentTeam] = useState(null); // {team, matches} | null (head-to-head)
  const [teamOrder, setTeamOrder] = useState('fp'); // the team's pick order
  const [patchKey, setPatchKey] = useState('current');

  const [allPool, setAllPool] = useState([]);
  const [loadingPool, setLoadingPool] = useState(false);

  // Load team list once.
  useEffect(() => {
    let alive = true;
    supabaseService.getProTeams()
      .then((t) => { if (alive) { setTeams(t); setLoadingTeams(false); } })
      .catch(() => { if (alive) setLoadingTeams(false); });
    return () => { alive = false; };
  }, []);

  // Load the selected team's full match history (date-filtered client-side).
  useEffect(() => {
    if (!selectedTeam) { setAllPool([]); return; }
    let alive = true;
    setLoadingPool(true);
    supabaseService.getTeamProMatches(selectedTeam.team)
      .then((rows) => { if (alive) { setAllPool(rows); setLoadingPool(false); } })
      .catch(() => { if (alive) { setAllPool([]); setLoadingPool(false); } });
    return () => { alive = false; };
  }, [selectedTeam]);

  // Opponents the scouted team has actually faced (for the head-to-head picker).
  const opponentOptions = useMemo(() => {
    if (!selectedTeam) return [];
    const counts = new Map();
    allPool.forEach((m) => {
      const opp = opponentOf(m, selectedTeam.team);
      if (opp) counts.set(opp, (counts.get(opp) || 0) + 1);
    });
    return Array.from(counts.entries())
      .map(([team, matches]) => ({ team, matches }))
      .sort((a, b) => b.matches - a.matches);
  }, [selectedTeam, allPool]);

  const fromDate = useMemo(() => windowFromDate(patchKey), [patchKey]);
  const pool = useMemo(() => {
    let rows = allPool;
    if (fromDate) rows = rows.filter((m) => (m.date || '') >= fromDate);
    if (opponentTeam && selectedTeam) {
      rows = rows.filter((m) => norm(opponentOf(m, selectedTeam.team)) === norm(opponentTeam.team));
    }
    return rows;
  }, [allPool, fromDate, opponentTeam, selectedTeam]);

  // Live picks from the embedded DraftBoard (current pick-mode, first scenario).
  const activeScenario = useMemo(() => {
    const list = pickMode === 'first' ? scenariosFirstPick : scenariosSecondPick;
    return (list && list[0]) || { picks: [] };
  }, [pickMode, scenariosFirstPick, scenariosSecondPick]);

  const picks = activeScenario.picks || [];

  const boardHeroNames = useMemo(() => {
    const s = new Set();
    picks.forEach((p) => { if (p && p.hero && p.hero.name) s.add(p.hero.name); });
    return s;
  }, [picks]);

  // Next chronological unfilled slot (1..24) and who owns it.
  const active = useMemo(() => {
    let slot = null;
    for (let i = 1; i <= 24; i++) { if (!picks[i - 1]) { slot = i; break; } }
    if (!slot) return null;
    const isFpSide = FP_SLOTS.has(slot);
    const owner = (isFpSide ? (teamOrder === 'fp') : (teamOrder === 'sp')) ? 'team' : 'you';
    const meta = teamSlots(owner === 'team' ? teamOrder : (teamOrder === 'fp' ? 'sp' : 'fp'), 'NEW')
      .find((m) => m.slot === slot);
    return { slot, owner, meta };
  }, [picks, teamOrder]);

  const prediction = useMemo(() => {
    if (!selectedTeam || !active || active.owner !== 'team' || loadingPool) return undefined;
    return predictNextMove({
      team: selectedTeam.team,
      teamOrder,
      moveType: active.meta.type,
      moveK: active.meta.k,
      boardHeroes: boardHeroNames,
      pool,
    });
  }, [selectedTeam, active, teamOrder, boardHeroNames, pool, loadingPool]);

  // One-click place a recommended hero onto the team's active slot (writes into
  // the embedded DraftBoard via DraftContext). isBanned follows the slot type.
  const placeOnBoard = (heroName) => {
    if (!active) return;
    const h = getHeroByName(heroName);
    const completeHero = h
      ? { id: h.id, name: h.name, imageUrl: h.imageUrl, attributes: h.attributes || {} }
      : { name: heroName };
    const newPicks = Array.from({ length: 24 }, (_, i) => picks[i] || null);
    newPicks[active.slot - 1] = { hero: completeHero, isBanned: active.meta.type === 'ban' };
    updateScenario(pickMode, activeScenario.id, { picks: newPicks });
  };

  return (
    <Page>
      <TopBar>
        <TitleWrap>
          <H1>Scout Simulator</H1>
          <Tagline>Draft on the board — suggestions for the scouted team come only from their historical pro drafts.</Tagline>
        </TitleWrap>
        {/* COMMERCIAL-DEMO: /simulator route disabled — back link goes to /drafts */}
        <BackLink onClick={() => navigate('/drafts')}>← Drafts</BackLink>
      </TopBar>

      <Setup>
        <Field>
          <FieldLabel>TEAM TO SCOUT</FieldLabel>
          <Autocomplete
            size="small"
            sx={{ width: 260 }}
            options={teams}
            loading={loadingTeams}
            getOptionLabel={(o) => `${o.team} (${o.matches})`}
            value={selectedTeam}
            onChange={(_, v) => { setSelectedTeam(v); setOpponentTeam(null); }}
            isOptionEqualToValue={(o, v) => o.team === v.team}
            renderInput={(params) => (
              <TextField {...params} placeholder={loadingTeams ? 'Loading teams…' : 'Pick a team'} />
            )}
          />
        </Field>

        <Field>
          <FieldLabel>PLAYING AGAINST (OPTIONAL)</FieldLabel>
          <Autocomplete
            size="small"
            sx={{ width: 240 }}
            options={opponentOptions}
            disabled={!selectedTeam}
            getOptionLabel={(o) => `${o.team} (${o.matches})`}
            value={opponentTeam}
            onChange={(_, v) => setOpponentTeam(v)}
            isOptionEqualToValue={(o, v) => o.team === v.team}
            renderInput={(params) => (
              <TextField {...params} placeholder={selectedTeam ? 'Any opponent' : 'Pick a team first'} />
            )}
          />
        </Field>

        <Field>
          <FieldLabel>THEIR PICK ORDER</FieldLabel>
          <Toggle>
            <ToggleBtn $on={teamOrder === 'fp'} onClick={() => setTeamOrder('fp')}>1st Pick</ToggleBtn>
            <ToggleBtn $on={teamOrder === 'sp'} onClick={() => setTeamOrder('sp')}>2nd Pick</ToggleBtn>
          </Toggle>
        </Field>

        <Field>
          <FieldLabel>HISTORY WINDOW</FieldLabel>
          <Select value={patchKey} onChange={(e) => setPatchKey(e.target.value)}>
            {Object.entries(PATCH_OPTIONS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </Select>
        </Field>
      </Setup>

      <Layout>
        <BoardHost>
          {/* isEmbedView keeps the board editable (click-to-pick) while hiding
              team/tournament inputs, pick-mode toggle and New Draft chrome.
              NOT isSimulatorView — that makes the board display-only. */}
          <DraftBoard isEmbedView hideToolbar />
        </BoardHost>

        <PanelCol>
          {!selectedTeam ? (
            <Placeholder>Pick a team above to see their historical draft tendencies as you fill the board.</Placeholder>
          ) : (
            <>
              <ScoutSuggestionPanel
                teamName={selectedTeam.team}
                activeMeta={active ? active.meta : null}
                owner={active ? active.owner : null}
                prediction={prediction}
                loading={loadingPool || prediction === undefined}
                onPlace={placeOnBoard}
              />
              <PoolNote>
                {loadingPool
                  ? 'Loading match history…'
                  : `${pool.length} of ${allPool.length} ${selectedTeam.team} games`
                    + (opponentTeam ? ` vs ${opponentTeam.team}` : '')
                    + ' in this window.'}
              </PoolNote>
            </>
          )}
        </PanelCol>
      </Layout>
    </Page>
  );
}
